/**
 * MainContent Component
 * Routes to appropriate content based on selected module
 * Created: 2025-12-23 18:30 EST
 *
 * This is the main content area that renders different views
 * based on the currently selected module.
 */

import { Suspense, lazy } from 'react'
import './MainContent.css'

// ============================================
// LAZY-LOADED MODULE COMPONENTS
// ============================================
// Core modules
const OddsTableModular = lazy(() => import('./OddsTableModular.jsx'))
const LandingPage = lazy(() => import('./LandingPage.jsx'))
const SystemStatus = lazy(() => import('./SystemStatus.jsx'))
const ProofBoardModule = lazy(() => import('../../components/proofBoard/ProofBoardModule.jsx'))

// New modular Systems Dashboard with standalone widgets
const SystemsDashboard = lazy(() => import('./systems/SystemsDashboard.jsx'))

// Fallback to original OddsTable if modular version has issues
const OddsTableOriginal = lazy(() => import('../../components/OddsTable.jsx'))

// Betting advantage modules
const ArbitrageModule = lazy(() => import('./ArbitrageModule.jsx'))
const MiddlesModule = lazy(() => import('./MiddlesModule.jsx'))
const PositiveEVModule = lazy(() => import('./PositiveEVModule.jsx'))

// Stats Module - Data exploration with charts
const StatsModule = lazy(() => import('./stats/StatsModule.jsx'))

// Master List - Full sportsbook catalog from books_comprehensive.json
const MasterListModule = lazy(() => import('./MasterListModule.jsx'))

// TODO: Create modular versions of these
// const ModelsModule = lazy(() => import('./modules/ModelsModule'))

// ============================================
// LOADING SPINNER
// ============================================
function LoadingSpinner() {
  return (
    <div className="module-loading">
      <div className="loading-spinner"></div>
      <span>Loading...</span>
    </div>
  )
}

// ============================================
// PLACEHOLDER FOR UNBUILT MODULES
// ============================================
function ModulePlaceholder({ moduleName, icon }) {
  return (
    <div className="module-placeholder">
      <i className={icon}></i>
      <h2>{moduleName}</h2>
      <p>This module is coming soon.</p>
    </div>
  )
}

// ============================================
// MODULE ICONS
// ============================================
const MODULE_ICONS = {
  Landing: 'fas fa-home',
  Odds: 'fas fa-percentage',
  ProofBoard: 'fas fa-table-cells',
  Stats: 'fas fa-chart-line',
  Arbitrage: 'fas fa-balance-scale',
  Middles: 'fas fa-compress-arrows-alt',
  PositiveEV: 'fas fa-chart-line',
  '+EV': 'fas fa-chart-line',
  Models: 'fas fa-brain',
  System: 'fas fa-cogs',
  Analytics: 'fas fa-chart-bar',
  Operations: 'fas fa-server',
  MasterList: 'fas fa-list',
  Data: 'fas fa-database',
  Infrastructure: 'fas fa-network-wired',
  Tools: 'fas fa-tools',
}

// ============================================
// MAIN CONTENT COMPONENT
// ============================================
function MainContent({
  selectedModule,
  selectedSport,
  selectedMarket,
  selectedDate,
  onVisibleDateChange,
  onModuleSelect,
  isLiveActive,
  isTodayActive,
  onAuthClick,
  user
}) {

  // Render the appropriate module
  const renderModule = () => {
    switch (selectedModule) {
      // ========================================
      // LANDING PAGE - Homepage/welcome screen
      // ========================================
      case 'Landing':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <LandingPage
              onModuleSelect={onModuleSelect}
              onAuthClick={onAuthClick}
              user={user}
            />
          </Suspense>
        )

      // ========================================
      // ODDS MODULE - Main odds comparison table
      // Using modular OddsTableModular with proper styling
      // ========================================
      case 'Odds':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <OddsTableModular
              sport={selectedSport}
              market={selectedMarket}
              selectedDate={selectedDate}
              onVisibleDateChange={onVisibleDateChange}
              filterToDate={false}
              isLiveActive={isLiveActive}
            />
          </Suspense>
        )

      // ========================================
      // PROOF BOARD MODULE - Proof portal shard bridge
      // ========================================
      case 'ProofBoard':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <ProofBoardModule />
          </Suspense>
        )

      // ========================================
      // STATS MODULE - Data exploration with charts
      // Browse PostgreSQL, QuestDB, MLB Warehouse data
      // ========================================
      case 'Stats':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <StatsModule />
          </Suspense>
        )

      // ========================================
      // ARBITRAGE MODULE - Find arbitrage opportunities
      // ========================================
      case 'Arbitrage':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <ArbitrageModule sport={selectedSport} />
          </Suspense>
        )

      // ========================================
      // MIDDLES MODULE - Find middle opportunities
      // ========================================
      case 'Middles':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <MiddlesModule sport={selectedSport} />
          </Suspense>
        )

      // ========================================
      // +EV MODULE - Positive expected value bets
      // ========================================
      case 'PositiveEV':
      case '+EV':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <PositiveEVModule sport={selectedSport} />
          </Suspense>
        )

      // ========================================
      // MODELS MODULE - Predictive models
      // ========================================
      case 'Models':
        return (
          <ModulePlaceholder
            moduleName="Prediction Models"
            icon={MODULE_ICONS.Models}
          />
        )

      // ========================================
      // SYSTEM MODULE - System status dashboard
      // Uses new modular SystemsDashboard with standalone widgets
      // Each widget is independent: ReceiverWidget, ScraperWidget, etc.
      // ========================================
      case 'System':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <SystemsDashboard />
          </Suspense>
        )

      // ========================================
      // MASTER LIST MODULE - Full sportsbook catalog
      // ========================================
      case 'MasterList':
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <MasterListModule />
          </Suspense>
        )

      // ========================================
      // OTHER MODULES
      // ========================================
      default:
        return (
          <ModulePlaceholder
            moduleName={selectedModule}
            icon={MODULE_ICONS[selectedModule] || 'fas fa-cube'}
          />
        )
    }
  }

  return (
    <main className="main-content">
      {renderModule()}
    </main>
  )
}

export default MainContent
